import React, { useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import Table from "src/componets/Table";
import { useAppSelector, useAppDispatch } from "src/store/hooks";
import { ColumnsType } from "antd/es/table";
import { CdrInterface } from "src/store/callrecorder/cdrsSlice";
import { fetchCdrs } from "src/actions/callrecorder/cdrsActions";
import { queryParams } from "src/helpers/UrlHelpers";
import { formattedDate } from "src/helpers/DateHelpers";
import Pagination from "src/componets/Pagination";
import OptionalLink from "src/componets/OptionalLink";
import callrecorderUrls from "src/configs/callrecorder/callrecorderUrls";

import { Tag } from "antd";

interface ListTableInterface {
  className?: string;
  limit: number;
  filterParams?: object;
}

const _ListTable = ({ className, limit, filterParams }: ListTableInterface) => {
  const dispatch = useAppDispatch();
  const [searchParams] = useSearchParams();
  const currentPage = queryParams.getInt(searchParams, 'page') || 1;
  const ids = useAppSelector(state => state.callrecorder.cdrs.ids);
  const entities = useAppSelector(state => state.callrecorder.cdrs.entities);
  const count = useAppSelector(state => state.callrecorder.cdrs.count);
  const data = ids ? ids.map(id => entities[id]) : [];

  const urlTerm = searchParams.get('term') || '';
  const urlSearchBy = searchParams.get('search_by') || '';
  const urlOrderBy = searchParams.get('order_by') || '';
  const urlOrder = searchParams.get('order') || '';

  const params = useMemo(() => ({
    limit,
    offset: (currentPage - 1) * limit,
    ...filterParams,
    ...(urlTerm ? { term: urlTerm, search_by: urlSearchBy } : {}),
    ...(urlOrderBy ? { order_by: urlOrderBy, order: urlOrder } : {})
  }), [limit, currentPage, filterParams, urlTerm, urlSearchBy, urlOrderBy, urlOrder]);

  useEffect(() => {
    if (filterParams) {
      dispatch(fetchCdrs(params));
    }
  }, [params, filterParams, dispatch]);

  const dispositionColor = (disposition: string | null) => {
    switch (disposition) {
      case 'ANSWERED': return 'success';
      case 'NO ANSWER': return 'warning';
      case 'BUSY': return 'orange';
      case 'FAILED': return 'error';
      case 'CANCEL': return 'default';
      default: return 'default';
    }
  };

  const callTypeColor = (callType: string | null) => {
    switch (callType) {
      case 'incoming': return 'blue';
      case 'outgoing': return 'green';
      case 'reminder': return 'purple';
      default: return 'default';
    }
  };

  const formatDuration = (seconds: number) => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return m > 0 ? `${m}m ${s}s` : `${s}s`;
  };

  const columns: ColumnsType<CdrInterface> = [
    {
      key: 'id',
      title: 'ID',
      render: (_, { id, url }) => (
        <OptionalLink link={url}>
          <small>{`#${id}`}</small>
        </OptionalLink>
      ),
      width: 70
    },

    {
      key: 'user_id',
      title: 'User',
      render: (_, { user_id }) => user_id ? (
        <OptionalLink link={callrecorderUrls.userPath(user_id)}>
          <small>{`#${user_id}`}</small>
        </OptionalLink>
      ) : '—',
      width: 60
    },

    {
      key: 'disposition',
      title: 'Disposition',
      render: (_, { disposition }) => disposition ? <Tag color={dispositionColor(disposition)}>{disposition}</Tag> : '—',
      width: 100
    },

    {
      key: 'call_type',
      title: 'Type',
      render: (_, { call_type }) => call_type ? <Tag color={callTypeColor(call_type)}>{call_type}</Tag> : '—',
      width: 90
    },

    {
      key: 'src',
      title: 'Src',
      render: (_, { src }) => src ? <small>{src}</small> : '—',
      width: 120
    },

    {
      key: 'dst',
      title: 'Dst',
      render: (_, { dst }) => dst ? <small>{dst}</small> : '—',
      width: 120
    },

    {
      key: 'duration',
      title: 'Duration',
      render: (_, { duration }) => <small>{formatDuration(duration)}</small>,
      width: 70
    },

    {
      key: 'billsec',
      title: 'Billsec',
      render: (_, { billsec }) => <small>{formatDuration(billsec)}</small>,
      width: 70
    },

    {
      key: 'ipaddr',
      title: 'IP',
      render: (_, { ipaddr }) => ipaddr ? <small>{ipaddr}</small> : '—',
      width: 110
    },

    {
      key: 'start_call',
      title: 'Start Call',
      render: (_, { start_call }) => start_call ? <small>{formattedDate(start_call).dateTime}</small> : '—',
      width: 125
    },
  ];

  return (
    <div className={className}>
      <Table
        rowKey='id'
        columns={columns}
        dataSource={data}
        search_keys={['user_id', 'src', 'dst', 'disposition', 'ipaddr']}
        sort_keys={['id', 'duration', 'billsec', 'start_call']}
      />

      <Pagination className='v-mt-10' total={count} size={limit} />
    </div>
  )
}

export default _ListTable;
